import React, { useState } from 'react';
import { Button } from '../../Button';
import { ModalShell } from '../../cockpit/CockpitBits';
import { describeError } from '../../../services/errorMessage';
import {
    ARTIKEL_HINWEIS_MAX,
    ARTIKEL_LAGERORT_MAX,
    ARTIKEL_NAME_MAX,
    artikelEingabeAus,
    pruefeArtikelEingabe,
    zeichenZahl,
    type ArtikelEingabe,
    type ArtikelFeld,
    type ArtikelWerte,
} from '../../../services/material/materialLogic';
import type { MaterialArtikel } from '../../../services/material/materialTypes';

/**
 * Artikel anlegen (`artikel` null) oder bearbeiten. Geprüft wird wie in der
 * Tabelle (`pruefeArtikelEingabe`); gespeichert über den Rückruf, dessen
 * Fehler im Dialog stehen bleibt.
 */

interface Props {
    artikel: MaterialArtikel | null;
    onSpeichern: (werte: ArtikelWerte) => Promise<unknown>;
    onSchliessen: () => void;
}

const FELD = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 disabled:bg-slate-50';
const LABEL = 'block text-[11px] font-bold text-slate-500 uppercase mb-1';

const Zaehler: React.FC<{ text: string; max: number }> = ({ text, max }) => {
    const n = zeichenZahl(text.trim());
    return <span className={`text-[11px] tabular-nums ${n > max ? 'text-red-700 font-semibold' : 'text-slate-400'}`}>{n}/{max}</span>;
};

export const ArtikelDialog: React.FC<Props> = ({ artikel, onSpeichern, onSchliessen }) => {
    const [eingabe, setEingabe] = useState<ArtikelEingabe>(() => artikelEingabeAus(artikel));
    const [feldFehler, setFeldFehler] = useState<Partial<Record<ArtikelFeld, string>>>({});
    const [fehler, setFehler] = useState('');
    const [speichert, setSpeichert] = useState(false);

    const aendern = (feld: ArtikelFeld, wert: string) => {
        setEingabe(e => ({ ...e, [feld]: wert }));
        setFeldFehler(f => ({ ...f, [feld]: undefined }));
    };

    const speichern = async (ev: React.FormEvent) => {
        ev.preventDefault();
        if (speichert) return;
        const pruefung = pruefeArtikelEingabe(eingabe);
        const werte = pruefung.werte;
        if (!werte) {
            setFeldFehler(pruefung.fehler);
            return;
        }
        setSpeichert(true);
        setFehler('');
        try {
            await onSpeichern(werte);
            onSchliessen();
        } catch (e) {
            setFehler(describeError(e));
            setSpeichert(false);
        }
    };

    const schliessen = () => { if (!speichert) onSchliessen(); };

    return (
        <ModalShell
            title={artikel ? 'Artikel bearbeiten' : 'Neuer Artikel'}
            width="max-w-lg"
            onClose={schliessen}
            footer={(
                <>
                    <Button type="button" variant="ghost" onClick={schliessen} disabled={speichert}>Abbrechen</Button>
                    <Button type="submit" form="material-artikel-form" loading={speichert}>Speichern</Button>
                </>
            )}
        >
            <form id="material-artikel-form" onSubmit={(ev) => { void speichern(ev); }} className="space-y-4" noValidate>
                <div>
                    <div className="flex items-baseline justify-between">
                        <label htmlFor="material-artikel-name" className={LABEL}>Name</label>
                        <Zaehler text={eingabe.name} max={ARTIKEL_NAME_MAX} />
                    </div>
                    <input
                        id="material-artikel-name"
                        className={FELD}
                        value={eingabe.name}
                        onChange={e => aendern('name', e.target.value)}
                        placeholder="z. B. Kopierpapier A4"
                        disabled={speichert}
                        autoFocus
                        aria-invalid={!!feldFehler.name}
                    />
                    {feldFehler.name && <p className="mt-1 text-xs text-red-700">{feldFehler.name}</p>}
                </div>
                <div>
                    <div className="flex items-baseline justify-between">
                        <label htmlFor="material-artikel-lagerort" className={LABEL}>Lagerort</label>
                        <Zaehler text={eingabe.lagerort} max={ARTIKEL_LAGERORT_MAX} />
                    </div>
                    <input
                        id="material-artikel-lagerort"
                        className={FELD}
                        value={eingabe.lagerort}
                        onChange={e => aendern('lagerort', e.target.value)}
                        placeholder="z. B. Büro, Schrank 2"
                        disabled={speichert}
                        aria-invalid={!!feldFehler.lagerort}
                    />
                    {feldFehler.lagerort && <p className="mt-1 text-xs text-red-700">{feldFehler.lagerort}</p>}
                </div>
                <div>
                    <div className="flex items-baseline justify-between">
                        <label htmlFor="material-artikel-hinweis" className={LABEL}>Hinweis</label>
                        <Zaehler text={eingabe.hinweis} max={ARTIKEL_HINWEIS_MAX} />
                    </div>
                    <textarea
                        id="material-artikel-hinweis"
                        className={`${FELD} min-h-[5rem]`}
                        value={eingabe.hinweis}
                        onChange={e => aendern('hinweis', e.target.value)}
                        placeholder="z. B. Meldebestand: 2 Kartons"
                        disabled={speichert}
                        aria-invalid={!!feldFehler.hinweis}
                    />
                    <p className="mt-1 text-[11px] text-slate-400">Erscheint auf der Karte und auf der Meldeseite.</p>
                    {feldFehler.hinweis && <p className="mt-1 text-xs text-red-700">{feldFehler.hinweis}</p>}
                </div>
                {artikel && (
                    <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-xl p-3">
                        Gedruckte Karten zeigen den alten Text, bis sie neu gedruckt werden. Der QR-Code bleibt gültig.
                    </p>
                )}
                {fehler && <p role="alert" className="text-xs text-red-700">{fehler}</p>}
            </form>
        </ModalShell>
    );
};
